import { SVGLayoutProps, SVGLayout } from "./Elements/SVGLayout";
import { CanvasCommand, CreateLayoutCommand } from "./command";
import { Image } from "./Elements/Image";
import { Frame } from "./Elements/Frame";
import { Group } from "./Elements/Group";
import { Textbox } from "./Elements/Textbox";
import { Illustration } from "./Elements/Illustration";
import { generate } from "./util/guid";

import {
    LayoutsState,
    ILayout,
    LayoutElement,
    ImageElement,
    FrameElement,
    IllustrationElement,
    TextboxElement,
    RenderingConfiguration,
    LayoutState,
    Layerable,
    PapyrusNode,
    PapyrusComponent,
    LayoutElementType,
    GroupElement,
} from "./types";

export interface CommandState {
    transaction: LayoutsState;
}

export interface LayoutComponentConfiguration {
    renderingConfiguration: RenderingConfiguration;
    outlineArea?: {
        x?: number;
        y?: number;
        width?: number;
        height?: number;
        scale?: number;
        // If true the outline area won't be stroked.
        hidden?: boolean;
    };
    viewBox?: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    maxHeight?: string;
    maxWidth?: string;
    height?: string | number;
    width?: string | number;
    position?: string;
    borderRadius?: number;
    outlineColor?: string;
    backgroundColor?: string;
}

export class CommandContext {
    private state: undefined | CommandState;
    private stateCallbacks: (() => void)[];
    private id: string;

    private prevCommands: CanvasCommand[];
    private nextCommands: CanvasCommand[];

    constructor() {
        this.id = generate();
        this.stateCallbacks = [];
        this.prevCommands = [];
        this.nextCommands = [];
    }

    registerStateCallback(callback: () => void) {
        this.stateCallbacks.push(callback);
    }

    unregisterStateCallback(callback: () => void) {
        this.stateCallbacks.filter((cb) => cb !== callback);
    }

    getState() {
        return this.state;
    }

    private runStateCallbacks() {
        this.stateCallbacks.forEach((cb) => cb());
    }

    apply(command: CanvasCommand, leaveOffUndoStack?: boolean) {
        if (!this.state) {
            throw new Error(`State not initialized for cc ${this.id}!`);
        }
        this.state = this.commandReducer(this.state, command);
        this.runStateCallbacks();
        this.nextCommands = [];
        if (!leaveOffUndoStack) {
            this.prevCommands.push(command);
        }
    }

    undo() {
        if (!this.state) {
            return;
        }
        const mostRecentCommand = this.prevCommands.pop();
        if (!mostRecentCommand) {
            return;
        }
        const undoneState = mostRecentCommand.undo();
        this.state = { ...this.state, transaction: undoneState };
        this.nextCommands.push(mostRecentCommand);
        this.runStateCallbacks();
    }

    redo() {
        if (!this.state) {
            return;
        }
        const mostImminentCommand = this.nextCommands.pop();
        if (!mostImminentCommand) {
            return;
        }
        this.state = this.commandReducer(this.state, mostImminentCommand);
        this.runStateCallbacks();
        this.prevCommands.push(mostImminentCommand);
    }

    /**
     * Find all commands in history with the given sequence ID,
     * discard all but the last and overwrite its oldState.
     */
    public flattenSequence(sequenceId: string, initialState: LayoutsState) {
        const commandSequence = this.prevCommands.filter((c) => c.sequenceId === sequenceId);
        const mostRecentCommand = commandSequence.pop();
        if (!mostRecentCommand) {
            return;
        }
        this.prevCommands = this.prevCommands.filter((c) => c.sequenceId !== sequenceId);
        mostRecentCommand?.overrideOldState(initialState);
        this.prevCommands.push(mostRecentCommand);
    }

    getLayoutById(layoutId: string): LayoutData {
        if (!this.state) {
            throw new Error(`State not initialized for cc ${this.id}!`);
        }
        const transactionState = this.state.transaction?.layouts?.[layoutId];
        const layout = transactionState?.layout;
        if (!layout) {
            throw new Error(`No layout: ${layoutId}`);
        }
        const els = [...(transactionState?.elements || [])];
        return this.getLayoutDataWithState(layout, els);
    }

    getAllLayouts() {
        if (!this.state) {
            throw new Error(`State not initialized for cc ${this.id}!`);
        }
        const layoutNamesWithDupes = [...Object.keys(this.state.transaction?.layouts)];
        const layoutNames = [...new Set(layoutNamesWithDupes)];
        return layoutNames.map((id) => this.getLayoutById(id));
    }

    private getLayoutDataWithState(layout: ILayout, elements: LayoutElement[]) {
        return {
            layoutState: {
                layout,
                elements,
            },
            getComponentWithProps: (configuration: LayoutComponentConfiguration) => {
                return getSvgElement(layout, elements, configuration);
            },
        } as LayoutData;
    }

    initialize(layouts: ILayout[], reloadedState?: LayoutsState) {
        if (reloadedState) {
            this.state = {
                transaction: reloadedState,
            };
            return;
        }
        let newState: LayoutsState = {
            serializableWorkflow: { steps: [] },
            layouts: {},
        };
        layouts.forEach((layout) => {
            newState = new CreateLayoutCommand(layout).apply(newState);
        });
        this.runStateCallbacks();
        this.state = {
            transaction: newState,
        };
    }

    private commandReducer(state: CommandState, command: CanvasCommand): CommandState {
        const transactionState = state.transaction || { layouts: {}, serializableWorkflow: { steps: [] } };
        return { ...state, transaction: command.apply(transactionState) };
    }
}

export const elementFactory = (element: LayoutElement): PapyrusNode => {
    if (element.type === LayoutElementType.Image) {
        return <Image key={element.id} {...(element as ImageElement)} />;
    }
    if (element.type === LayoutElementType.Frame) {
        return <Frame key={element.id} {...(element as FrameElement)} />;
    }
    if (element.type === LayoutElementType.Illustration) {
        return <Illustration key={element.id} {...(element as IllustrationElement)} />;
    }
    if (element.type === LayoutElementType.Textbox) {
        return <Textbox key={element.id} {...(element as TextboxElement)} />;
    }
    if (element.type === LayoutElementType.Group) {
        return <Group key={element.id} {...(element as GroupElement)} />;
    }
    return null;
};

export const getSvgElement = (
    layout: ILayout,
    elements: LayoutElement[],
    configuration: LayoutComponentConfiguration,
): PapyrusNode => {
    const renderingConfiguration = configuration.renderingConfiguration;

    // Filter elements if required, any element can be tagged with exclude for print, meaning
    // that its value will not be sent up to the server.
    const filteredElements = renderingConfiguration?.removeExcludedElements
        ? elements.filter((el) => !el.excludeFromExport)
        : elements;

    return (
        <SVGLayout
            preserveAspectRatio={undefined}
            elements={filteredElements}
            backgroundColor={
                layout.transparentBackground
                    ? undefined
                    : configuration.backgroundColor
                    ? configuration.backgroundColor
                    : "white"
            }
            width={configuration.width || layout.width}
            height={configuration.height || layout.height}
            viewBox={
                renderingConfiguration?.region
                    ? {
                          x: renderingConfiguration.region.left,
                          y: renderingConfiguration.region.top,
                          width: renderingConfiguration.region.width,
                          height: renderingConfiguration.region.height,
                      }
                    : {
                          x: 0,
                          y: 0,
                          width: layout.width,
                          height: layout.height,
                      }
            }
            configuration={renderingConfiguration}
            outlineArea={configuration.outlineArea}
            maxHeight={configuration.maxHeight}
            maxWidth={configuration.maxWidth}
            position={configuration.position}
            borderRadius={configuration.borderRadius}
            outlineColor={configuration.outlineColor}
        />
    );
};

/**
 * Computes a list of elements sorted by layers first, lowest to highest. Followed by index within that layer, lowest to highest.
 * @returns The original array reference. Sorted in place.
 */
export function sortElementsByLayersWithIndex<T extends Layerable>(items: T[]): T[] {
    return items.sort((a, b) => {
        // Sort by layer first as this is the more significant factor.
        const aLayer = a.layer || 0;
        const bLayer = b.layer || 0;
        if (aLayer < bLayer) return -1;
        if (aLayer > bLayer) return 1;
        // If layer is equal, sort by index instead. Elements on the same layer
        // may be re-ordered amongst eachother.
        const aLayerIndex = a.layerIndex || 0;
        const bLayerIndex = b.layerIndex || 0;
        if (aLayerIndex < bLayerIndex) return -1;
        if (aLayerIndex > bLayerIndex) return 1;
        // Otherwise, they are at exactly the same place from a layering perspective, this actually isn't good. It means
        // we would be relying on stable sorting to ensure the elements don't fall out of the order we would expect.
        return 0;
    });
}

export interface LayoutData {
    layoutState: LayoutState;
    Component: PapyrusComponent<SVGLayoutProps>;
    getComponentWithProps: (configuration: LayoutComponentConfiguration) => PapyrusComponent<SVGLayoutProps>;
}
